/**
 * Assistant content normalization and transcript helpers used by the Rax chat UI.
 */

export interface StreamToolEvent {
  phase: string;
  name?: string;
  meta?: string;
  isError?: boolean;
  /** Optional tool arguments for enhanced rendering (e.g., personaId for sessions_spawn) */
  args?: Record<string, unknown>;
}

/**
 * Parsed execution plan step
 */
export interface ParsedPlanStep {
  index: number;
  description: string;
  assignedTo?: string;
  status: "pending" | "in_progress" | "completed" | "error";
}

/**
 * Parsed execution plan from assistant output
 */
export interface ParsedPlan {
  title?: string;
  reasoning?: string;
  steps: ParsedPlanStep[];
}

const STATUS_MARKER_PATTERN = /(🕒|🧠|🔑|🧮|📚|🧹|🧵|⚙️|🪢)/g;
const STATUS_KEYWORD_PATTERN =
  /(Time:|Model:|Tokens:|Context:|Compactions:|Session:|Runtime:|Queue:)/g;
const ANSI_ESCAPE_PATTERN = /\u001b\[[0-9;?]*[ -/]*[@-~]/g;
const ANSI_OSC_PATTERN = /\u001b\][^\u0007]*(?:\u0007|\u001b\\)/g;

function normalizeMalformedCodeFences(content: string): string {
  let normalized = content;
  normalized = normalized.replace(/```([a-zA-Z0-9_-]+)[ \t]+/g, "```$1\n");
  normalized = normalized.replace(/```[ \t]+/g, "```\n");
  normalized = normalized.replace(/[ \t]+```/g, "\n```");
  return normalized;
}

export function normalizeAssistantContent(content: string): string {
  let normalized = content
    .replace(ANSI_OSC_PATTERN, "")
    .replace(ANSI_ESCAPE_PATTERN, "")
    .replace(/🦞\s*OpenClaw\b/g, "⚡ Rax")
    .replace(/🦞\s*Rax\b/g, "⚡ Rax")
    .replace(/\bOpenClaw\b/g, "Rax");

  if (!normalized.includes("\n") && normalized.includes("\\n")) {
    normalized = normalized.replace(/\\n/g, "\n");
  }
  if (normalized.includes("\\`\\`\\`")) {
    normalized = normalized.replace(/\\`\\`\\`/g, "```");
  }
  normalized = normalizeMalformedCodeFences(normalized);

  const statusMarkerCount = (normalized.match(STATUS_MARKER_PATTERN) || []).length;
  const statusKeywordCount = (normalized.match(STATUS_KEYWORD_PATTERN) || []).length;
  const looksLikeInlineStatus = statusMarkerCount >= 3 && !normalized.includes("\n");
  if (looksLikeInlineStatus) {
    normalized = normalized.replace(/\s+(🕒|🧠|🔑|🧮|📚|🧹|🧵|⚙️|🪢)\s*/g, "\n$1 ");
  }
  const looksLikeInlineKeywordStatus = statusKeywordCount >= 3 && !normalized.includes("\n");
  if (looksLikeInlineKeywordStatus) {
    normalized = normalized.replace(
      /\s+(Time:|Model:|Tokens:|Context:|Compactions:|Session:|Runtime:|Queue:)\s*/g,
      "\n$1 ",
    );
  }

  const hasBoxDrawing = /[┌┐└┘│─]/.test(normalized);
  const looksLikeStatusCard =
    /(?:^|\n)⚡\s+Rax\b/.test(normalized) &&
    (/(?:^|\n)(?:🕒|Time:|Model:|Session:)/m.test(normalized) ||
      statusMarkerCount >= 3 ||
      statusKeywordCount >= 3);
  const hasCodeFence = /```/.test(normalized);

  if ((hasBoxDrawing || looksLikeStatusCard) && !hasCodeFence) {
    return `\`\`\`text\n${normalized.trim()}\n\`\`\``;
  }
  return normalized;
}

// Maps tool names to human-friendly descriptions
const TOOL_DESCRIPTIONS: Record<string, string> = {
  Read: "Reading file",
  Write: "Writing file",
  Edit: "Editing file",
  Glob: "Finding files",
  Grep: "Searching code",
  Bash: "Running command",
  WebFetch: "Fetching webpage",
  WebSearch: "Searching the web",
  Task: "Running sub-task",
  TodoWrite: "Updating task list",
  AskUserQuestion: "Asking question",
  sessions_spawn: "Spawning agent",
  sessions_send: "Messaging agent",
  sessions_list: "Listing sessions",
  sessions_history: "Fetching history",
  // Document generation
  generate_document: "Generating document",
  // Thinking/proposal phases
  thinking: "Thinking",
  proposal: "Proposing approach",
  analyzing: "Analyzing request",
  planning: "Planning execution",
  iterating: "Iterating on solution",
  reviewing: "Reviewing work",
};

// Maps persona IDs to display info
const PERSONA_INFO: Record<string, { icon: string; name: string }> = {
  researcher: { icon: "🔍", name: "Researcher" },
  coder: { icon: "💻", name: "Coder" },
  analyst: { icon: "📊", name: "Analyst" },
  communicator: { icon: "✍️", name: "Communicator" },
  support: { icon: "🤝", name: "Support" },
  legal: { icon: "⚖️", name: "Legal" },
  security: { icon: "🔒", name: "Security" },
  sales: { icon: "💰", name: "Sales" },
  finance: { icon: "📈", name: "Finance" },
  people: { icon: "👥", name: "People" },
  pm: { icon: "📋", name: "PM" },
  bi: { icon: "🎯", name: "BI" },
};

function getToolDescription(toolName: string): string {
  return TOOL_DESCRIPTIONS[toolName] || toolName;
}

/**
 * Get persona-specific description for sessions_spawn calls
 */
function getSpawnDescription(args?: Record<string, unknown>): { icon: string; description: string } {
  const personaId = typeof args?.personaId === "string" ? args.personaId : "";
  const label = typeof args?.label === "string" ? args.label : "";
  const task = typeof args?.task === "string" ? args.task : "";

  const persona = PERSONA_INFO[personaId];
  if (persona) {
    const taskHint = label || (task.length > 50 ? `${task.slice(0, 47)}...` : task);
    return {
      icon: persona.icon,
      description: taskHint ? `${persona.name}: ${taskHint}` : `Delegating to ${persona.name}`,
    };
  }

  return { icon: "🔄", description: "Spawning sub-agent" };
}

/**
 * Render a thinking/proposal step for display
 */
function renderThinkingStep(event: StreamToolEvent): string | null {
  const phase = event.phase.trim().toLowerCase();
  const name = event.name?.trim() || "thinking";
  const meta = event.meta?.trim();

  const thinkingIcon = "💭";
  const proposalIcon = "📝";
  const icon = name === "proposal" ? proposalIcon : thinkingIcon;
  const description = TOOL_DESCRIPTIONS[name] || name;

  if (phase === "start") {
    return `\n<span class="thinking-step in-progress thinking-phase"><span class="step-icon">${icon}</span> ${description}...</span>\n`;
  }

  if (phase === "complete" || phase === "result") {
    if (meta) {
      // Show the proposal/thinking content
      const normalizedMeta = normalizeAssistantContent(meta).trim();
      return `\n<span class="thinking-step completed thinking-phase"><span class="step-icon">✓</span> ${description}</span>\n<div class="proposal-content">\n\n${normalizedMeta}\n\n</div>\n`;
    }
    return `\n<span class="thinking-step completed thinking-phase"><span class="step-icon">✓</span> ${description}</span>\n`;
  }

  return null;
}

export function renderToolEventTranscriptLine(event: StreamToolEvent): string | null {
  const phase = event.phase.trim().toLowerCase();
  const name = event.name?.trim();
  if (!name || phase === "approval-pending") {
    return null;
  }

  // Handle thinking/proposal phases specially
  const thinkingPhases = ["thinking", "proposal", "analyzing", "planning", "iterating", "reviewing"];
  if (thinkingPhases.includes(name.toLowerCase())) {
    return renderThinkingStep(event);
  }

  // Special handling for sessions_spawn to show persona info
  if (name === "sessions_spawn") {
    const { icon, description } = getSpawnDescription(event.args);

    if (phase === "start") {
      return `\n<span class="thinking-step in-progress persona-spawn"><span class="step-icon">${icon}</span> ${description}...</span>\n`;
    }

    if (phase === "result") {
      const statusIcon = event.isError ? "⚠️" : "✓";
      const statusClass = event.isError ? "error" : "completed";
      return `\n<span class="thinking-step ${statusClass} persona-spawn"><span class="step-icon">${statusIcon}</span> ${description}</span>\n`;
    }

    if (phase === "error" || event.isError) {
      return `\n<span class="thinking-step error persona-spawn"><span class="step-icon">✗</span> ${description} failed</span>\n`;
    }

    return null;
  }

  const description = getToolDescription(name);

  if (phase === "start") {
    // Render as an in-progress step with animated indicator
    return `\n<span class="thinking-step in-progress"><span class="step-icon">⋯</span> ${description}...</span>\n`;
  }

  if (phase === "result") {
    const meta = event.meta?.trim();
    if (meta) {
      const icon = event.isError ? "⚠️" : "✓";
      const statusClass = event.isError ? "error" : "completed";
      const normalizedMeta = normalizeAssistantContent(meta).trim();
      // For results with metadata, show collapsible details
      const body = normalizedMeta.includes("```")
        ? `\n${normalizedMeta}\n`
        : `\n\`\`\`\n${normalizedMeta}\n\`\`\`\n`;
      return `\n<span class="thinking-step ${statusClass}"><span class="step-icon">${icon}</span> ${description}</span>${body}`;
    }
    const icon = event.isError ? "⚠️" : "✓";
    const statusClass = event.isError ? "error" : "completed";
    return `\n<span class="thinking-step ${statusClass}"><span class="step-icon">${icon}</span> ${description}</span>\n`;
  }

  if (phase === "error" || event.isError) {
    const meta = event.meta?.trim();
    const errorDetail = meta ? `: ${meta}` : "";
    return `\n<span class="thinking-step error"><span class="step-icon">✗</span> ${description} failed${errorDetail}</span>\n`;
  }

  return null;
}

/**
 * Parse a numbered plan from assistant text
 */
export function parsePlanFromContent(content: string): ParsedPlan | null {
  // Look for plan patterns:
  // ## Plan / ## Steps / ## Approach
  // 1. First step
  // 2. Second step
  const planHeaderPattern = /(?:^|\n)(?:##?\s*)?(?:Plan|Steps|Approach|I'll|Let me)[:\s]*([^\n]*)/i;
  const numberedStepPattern = /^\s*(\d+)\.\s+(.+)$/gm;
  const bulletStepPattern = /^\s*[-*]\s+(.+)$/gm;

  const steps: ParsedPlanStep[] = [];
  let match: RegExpExecArray | null;

  // Try numbered format first
  while ((match = numberedStepPattern.exec(content)) !== null) {
    const index = parseInt(match[1], 10);
    const description = match[2].trim();
    // Skip if it looks like a result/output line rather than a plan step
    if (description && !description.startsWith("```") && description.length < 200) {
      // Check if step mentions a persona
      let assignedTo: string | undefined;
      for (const [personaId, info] of Object.entries(PERSONA_INFO)) {
        if (description.toLowerCase().includes(personaId) ||
            description.includes(info.icon) ||
            description.toLowerCase().includes(info.name.toLowerCase())) {
          assignedTo = personaId;
          break;
        }
      }
      steps.push({
        index,
        description,
        assignedTo,
        status: "pending",
      });
    }
  }

  // If no numbered steps found, try bullet format
  if (steps.length === 0) {
    let index = 1;
    while ((match = bulletStepPattern.exec(content)) !== null) {
      const description = match[1].trim();
      if (description && !description.startsWith("```") && description.length < 200) {
        let assignedTo: string | undefined;
        for (const [personaId, info] of Object.entries(PERSONA_INFO)) {
          if (description.toLowerCase().includes(personaId) ||
              description.includes(info.icon) ||
              description.toLowerCase().includes(info.name.toLowerCase())) {
            assignedTo = personaId;
            break;
          }
        }
        steps.push({
          index,
          description,
          assignedTo,
          status: "pending",
        });
        index++;
      }
    }
  }

  // Need at least 2 steps to be a plan
  if (steps.length < 2) return null;

  // Try to extract title
  const headerMatch = content.match(planHeaderPattern);
  const title = headerMatch ? headerMatch[1].trim() : undefined;

  return {
    title,
    steps,
  };
}

/**
 * Render a plan as HTML with step indicators
 */
export function renderPlanAsHtml(plan: ParsedPlan, activeStepIndex?: number): string {
  const lines: string[] = [];

  lines.push('<div class="execution-plan">');

  if (plan.title) {
    lines.push(`<div class="plan-header"><span class="plan-icon">📋</span> <span class="plan-title">${plan.title}</span></div>`);
  }

  lines.push('<div class="plan-steps">');

  for (const step of plan.steps) {
    const isActive = activeStepIndex === step.index;
    const statusClass = isActive ? "in_progress" : step.status;
    const statusIcon = isActive ? "⋯" : (step.status === "completed" ? "✓" : (step.status === "error" ? "✗" : "○"));

    const personaInfo = step.assignedTo ? PERSONA_INFO[step.assignedTo] : null;
    const assigneeBadge = personaInfo
      ? `<span class="step-assignee"><span class="assignee-icon">${personaInfo.icon}</span> ${personaInfo.name}</span>`
      : "";

    lines.push(`<div class="plan-step ${statusClass}">`);
    lines.push(`<span class="step-number ${statusClass}">${statusIcon}</span>`);
    lines.push(`<span class="step-text">${step.description}</span>`);
    if (assigneeBadge) {
      lines.push(assigneeBadge);
    }
    lines.push('</div>');
  }

  lines.push('</div>');
  lines.push('</div>');

  return lines.join('\n');
}

/**
 * Detect if content contains a multi-step plan and extract it
 */
export function detectAndExtractPlan(content: string): { hasPlan: boolean; plan?: ParsedPlan; contentWithoutPlan?: string } {
  const plan = parsePlanFromContent(content);
  if (!plan) {
    return { hasPlan: false };
  }

  // Remove the plan steps from content to avoid duplication
  let cleanContent = content;
  for (const step of plan.steps) {
    const stepPattern = new RegExp(`^\\s*${step.index}\\.\\s+${escapeRegex(step.description.slice(0, 50))}[^\\n]*\\n?`, 'gm');
    cleanContent = cleanContent.replace(stepPattern, '');
  }

  return {
    hasPlan: true,
    plan,
    contentWithoutPlan: cleanContent.trim(),
  };
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
